import React, {MouseEvent, useCallback, useEffect, useRef, useState} from 'react';
import {NavigationProps} from "../constants/Props";
import Title from "../components/Title";
import Screen from "../components/Screen";
import Box, {Direction} from "../components/Box";
import {observer} from "mobx-react";
import RadioButtonInput from "../components/RadioButtonInput";
import {formatI18nKeys, toI18N} from "../utils/I18n";
import {IDeeplinkStore} from "../stores/DeeplinkStore";
import {IAuthStore} from "../stores/AuthStore";
import Text from "../components/Text";
import textStyles from "../css/Text.module.css";
import {isMobile} from "react-device-detect";
import classNames from "classnames";
import Alert, {AlertRefProps} from "../components/Alert";
import {IGroupStore} from "../stores/GroupStore";
import {showErrorToast, showInfoToast, usePrevious} from "../utils/HooksHelper";
import LoadingSpinner from "../components/LoadingSpinner";
import * as Analytics from "../utils/Analytics";
import Modals, {ModalsRefProps} from "../components/Modals";
import isUrl from "is-url";

interface Props extends NavigationProps {
    store: IDeeplinkStore,
    isInMiddleOfLoggingIn: boolean,
    authStore: IAuthStore,
    groupStore: IGroupStore,
    logout: (event: MouseEvent) => void,
    login: (event: MouseEvent) => void,
    resetLinkStore: () => void,
    fetchLink: (body: { link_id: string } | { select_id: string }, isFork?: boolean) => Promise<number | null>,
    openCreateGroup: (event: MouseEvent) => void
    openPhoneNumber: (event: MouseEvent) => void
    openSelectGroup: (event: MouseEvent) => void
}

function GetStartedScreen(props: Props) {

    // Props

    const {
        openPhoneNumber,
        openSelectGroup,
        isInMiddleOfLoggingIn,
        fetchLink,
        logout,
        login,
        authStore: {
            loggedIn,
            name,
            setReturnToGetStartedScreen
        },
        openCreateGroup,
        store: {
            setIsNewGroup,
            setHasInviter,
            hasInviter,

            isNewGroup,
            linkId
        },
        groupStore: {
            setIsOwner,
            fetchingDeeplink,
            setFetchingDeeplink,
        },
        onBack,
        isActive,
        resetLinkStore,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_get_started_page);
        }
    });

    const [isEdit, setIsEdit] = useState(false)
    const [isFork, setIsFork] = useState(false)

    // Refs

    const alertRef = useRef<AlertRefProps>(null);
    const modalsRef = useRef<ModalsRefProps>(null);

    // Callbacks

    const onCreateGroupDisabledClick = useCallback(() => {
        showInfoToast(alertRef, toI18N("getting_started_can_not_create_group_when_editing"))
    }, [alertRef]);

    const onLogin = useCallback(
        (event: MouseEvent) => {

            setReturnToGetStartedScreen(true); // In order to return back here after login
            login(event);

        }, [login, setReturnToGetStartedScreen]
    );

    const handleOnNext = useCallback(
        async (e) => {
            const isNewGroupWithoutInviter = !hasInviter && isNewGroup;
            const isNewGroupAndLogin = loggedIn && isNewGroup;
            if (isFork || isEdit) {
                showEditDeeplinkModal(e)
                return
            }
            if (isNewGroupAndLogin || isNewGroupWithoutInviter) {
                openCreateGroup(e)
            } else if (!loggedIn) {
                openPhoneNumber(e)
            } else {
                openSelectGroup(e)
            }
        },
        [loggedIn, openSelectGroup, isFork, isEdit, openCreateGroup, openPhoneNumber, hasInviter, isNewGroup],
    );

    const handleOnChange = useCallback(
        (value) => {
            switch (true) {
                case value === "yes_choose_from_group" :
                    setIsOwner(false);
                    setIsNewGroup(false);
                    setIsEdit(false)
                    setIsFork(false)
                    break;
                case value === "no_create_group" :
                    setIsOwner(true);
                    setIsNewGroup(true);
                    setIsEdit(false)
                    setIsFork(false)
                    break;
                case value === "edit_link" :
                    setIsEdit(true);
                    setIsFork(false)
                    break;
                case value === "fork_link" :
                    setIsEdit(false);
                    setIsFork(true);
                    break;
            }
        },
        [setIsNewGroup, setHasInviter, setIsOwner]
    );

    const showEditDeeplinkModal = useCallback((e) => {

        modalsRef && modalsRef.current && modalsRef.current.showTextInputModal(async (url: string) => {
            const urlObject = new URL(url)
            const parts = urlObject.pathname.split("/");
            let selectId = "";

            const showError = (error: string) => {
                modalsRef && modalsRef.current && modalsRef.current.hideActionModal();
                showErrorToast(alertRef, error);
            };

            if (!parts[parts.length - 1]) {
                const hostPart = urlObject.host.split(".");
                if (hostPart.length !== 3) {
                    showError(toI18N("select_group_link_was_not_found"));
                    return;
                }
                selectId = hostPart[0]
            }

            setFetchingDeeplink(true);

            const result = await fetchLink(selectId ? {select_id: selectId} : {link_id: parts[parts.length - 1]}, isFork);

            if (result) {
                setFetchingDeeplink(false);
            } // else link was fetched and store was re-initiated

            if (result === 400) {
                showError(toI18N("select_group_link_was_not_found"));
            } else if (result === 403) {
                showError(toI18N("select_group_you_are_not_permitted_to_edit_link"));
            }

        });

    }, [modalsRef, fetchLink, alertRef, setFetchingDeeplink, isFork]);


    return (
        <Screen
            title={"get_started_screen_header_title"}
            subtitle={"get_started_screen_header_subtitle"}
            onBack={onBack}
            disabledNext={isInMiddleOfLoggingIn}
            onNext={handleOnNext}>

            <Alert ref={alertRef}/>
            <Modals
                id={'select-group-screen'}
                modalTextInputValidation={isUrl}
                showLoadingIndicator={fetchingDeeplink}
                ref={modalsRef}/>

            <Box topMargin={isMobile ? 90 : 120} center>
                <Title shrink
                       title={toI18N("get_started_screen_title")}
                       subtitle={toI18N("get_started_screen_subtitle")}
                />
            </Box>

            <Box grow center sideMargin direction={Direction.Column}>
                <Box shrink>
                    <RadioButtonInput
                        onOptionChange={handleOnChange}
                        defaultValue={isNewGroup ? "no_create_group" : "yes_choose_from_group"}
                        title={toI18N("is_your_community_on_select")}
                        options={[
                            {
                                label: toI18N("yes_choose_from_group"),
                                value: "yes_choose_from_group"
                            },
                            {
                                label: toI18N("no_create_group"),
                                value: "no_create_group",
                            },
                            {
                                label: toI18N("edit_link"),
                                value: "edit_link"
                            },
                            {
                                label: toI18N("fork_link"),
                                value: "fork_link"
                            },
                        ]}/>
                </Box>


            </Box>

            <Box bottomMargin={44} sideMargin center>
                {
                    isInMiddleOfLoggingIn &&
                        <Box>
                            <Text size={14}>
                                <span className={textStyles.labelColor}>
                                    {formatI18nKeys('getting_started_logging_in', name)}
                                </span>
                            </Text>
                            <Box leftMargin={12}>
                                <LoadingSpinner />
                            </Box>
                        </Box>
                }
                {
                    name && !isInMiddleOfLoggingIn &&
                    <Text size={14}>
                        <span className={textStyles.labelColor}>
                            {formatI18nKeys('getting_started_logged_in_as', name)}
                        </span>
                        <span onClick={logout}
                              className={textStyles.link}>
                            {toI18N('getting_started_logout')}
                        </span>
                    </Text>
                }
                {
                    !name && !isInMiddleOfLoggingIn &&
                    <Text size={14}>
                        <span className={textStyles.labelColor}>
                            {toI18N('getting_started_edit_your_link_later')}
                        </span>
                        <span onClick={onLogin}
                              className={textStyles.link}>
                            {toI18N('getting_started_login')}
                        </span>
                        <br/>
                        <span className={classNames(textStyles.labelColor, textStyles.labelSmall)}>
                            {toI18N('getting_started_you_return_back_here')}
                        </span>
                    </Text>
                }
            </Box>
        </Screen>
    );
}

export default observer(GetStartedScreen);
